import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiService } from '../../../services';
import { utils } from '../../../utils';
import { ApiResponse } from '../../../types/api';
import ViewLayout from '../../../components/Common/ViewLayout';

interface QRCodeData {
  qrcode: string;
  equipamento: {
    id: number;
    nome: string;
    tag: string;
    tipo: string;
    localizacao: string;
  };
  empresa?: {
    nome: string;
    suporte_telefone: string;
  };
  qr_data: string;
}

interface EquipmentDetail {
  id: number;
  nome: string;
  codigo: string;
  tag: string;
  tipo: string;
  localizacao: string;
  status: string;
  fabricante: string;
  numero_serie: string;
  capacidade: string;
  ultima_calibracao: string;
  proxima_calibracao: string;
  observacoes: string;
  criado_em: string;
  atualizado_em: string;
}

const EquipmentView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [equipment, setEquipment] = useState<EquipmentDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'detalhes' | 'qrcode'>('detalhes');
  const [qrCodeData, setQRCodeData] = useState<QRCodeData | null>(null);
  const [isLoadingQRCode, setIsLoadingQRCode] = useState(false);
  const qrCodeRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadEquipment();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadEquipment = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await apiService.get(`EQUIPMENT_BY_ID`, { id: Number(id) }) as ApiResponse<any>;

      if (response.success && response.data) {
        setEquipment(response.data);
      } else {
        throw new Error(response.message || 'Erro ao carregar equipamento');
      }
    } catch (error) {
      console.error('Erro ao carregar equipamento:', error);
      setError('Erro ao carregar detalhes do equipamento');
      utils.showNotification('Erro ao carregar detalhes do equipamento', { type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const loadQRCode = async () => {
    if (!id || qrCodeData) return;

    try {
      setIsLoadingQRCode(true);
      const response = await apiService.getEquipmentQRCode(Number(id)) as ApiResponse<QRCodeData>;

      if (response.success && response.data) {
        setQRCodeData(response.data);
      } else {
        throw new Error(response.message || 'Erro ao gerar QR Code');
      }
    } catch (error) {
      console.error('Erro ao gerar QR Code:', error);
      utils.showNotification('Erro ao gerar QR Code do equipamento', { type: 'error' });
    } finally {
      setIsLoadingQRCode(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'qrcode' && !qrCodeData && !isLoadingQRCode) {
      loadQRCode();
    }
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDownloadQRCode = () => {
    if (!qrCodeData || !equipment) return;

    const link = document.createElement('a');
    const tagName = (equipment.tag || equipment.nome).replace(/[^a-zA-Z0-9]/g, '-');
    link.download = `qrcode-equipamento-${tagName}.png`;
    link.href = qrCodeData.qrcode;
    link.click();
  };

  const handlePrintQRCode = () => {
    if (!qrCodeData || !equipment) return;

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      utils.showNotification('Não foi possível abrir a janela de impressão. Verifique se pop-ups estão permitidos.', { type: 'error' });
      return;
    }

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>QR Code - ${equipment.nome}</title>
          <style>
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            body {
              font-family: Arial, sans-serif;
              padding: 20px;
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              min-height: 100vh;
            }
            .container {
              text-align: center;
              border: 2px solid #333;
              padding: 30px;
              border-radius: 10px;
              max-width: 400px;
            }
            .empresa-nome {
              font-size: 12px;
              color: #666;
              margin-bottom: 10px;
              text-transform: uppercase;
              letter-spacing: 1px;
            }
            .qr-code {
              margin: 15px 0;
            }
            .qr-code img {
              width: 200px;
              height: 200px;
            }
            .equipment-info {
              margin-top: 15px;
            }
            .equipment-info h2 {
              font-size: 20px;
              margin-bottom: 8px;
              color: #333;
            }
            .equipment-info p {
              font-size: 12px;
              color: #666;
              margin: 4px 0;
            }
            .tag {
              font-size: 24px !important;
              font-weight: bold;
              color: #000 !important;
              background: #f0f0f0;
              padding: 8px 16px;
              border-radius: 5px;
              display: inline-block;
              margin-top: 8px;
            }
            .suporte {
              margin-top: 15px;
              padding-top: 15px;
              border-top: 1px dashed #ccc;
              font-size: 11px;
              color: #666;
            }
            .suporte strong {
              color: #333;
            }
            @media print {
              body {
                padding: 0;
              }
              .container {
                border: 2px solid #000;
              }
            }
          </style>
        </head>
        <body>
          <div class="container">
            ${qrCodeData.empresa?.nome ? `<p class="empresa-nome">${qrCodeData.empresa.nome}</p>` : ''}
            <div class="equipment-info">
              <h2>${equipment.nome}</h2>
              <p>${equipment.tipo || ''}</p>
            </div>
            <div class="qr-code">
              <img src="${qrCodeData.qrcode}" alt="QR Code do Equipamento" />
            </div>
            <div class="equipment-info">
              ${equipment.tag ? `<p class="tag">${equipment.tag}</p>` : ''}
              ${equipment.localizacao ? `<p>Local: ${equipment.localizacao}</p>` : ''}
            </div>
            ${qrCodeData.empresa?.suporte_telefone ? `
            <div class="suporte">
              <strong>Suporte:</strong> ${qrCodeData.empresa.suporte_telefone}
            </div>
            ` : ''}
          </div>
          <script>
            window.onload = function() {
              window.print();
              window.onafterprint = function() {
                window.close();
              };
            };
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const formatDateTime = (dateString: string): string => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDate = (dateString: string): string => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const getStatusClass = (status: string): string => {
    const statusMap: Record<string, string> = {
      'ativo': 'status-active',
      'inativo': 'status-inactive',
      'manutencao': 'status-maintenance'
    };
    return statusMap[status] || 'status-pending';
  };

  const getStatusText = (status: string): string => {
    const statusMap: Record<string, string> = {
      'ativo': 'Ativo',
      'inativo': 'Inativo',
      'manutencao': 'Em Manutenção'
    };
    return statusMap[status] || status;
  };

  const getCalibrationStatus = (proximaCalibracao: string): { status: string; class: string; text: string } => {
    if (!proximaCalibracao) return { status: 'unknown', class: 'status-warning', text: 'Data não informada' };

    const calibrationDate = new Date(proximaCalibracao);
    const today = new Date();
    const diffTime = calibrationDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) {
      return { status: 'overdue', class: 'status-danger', text: 'Calibração Atrasada' };
    } else if (diffDays <= 7) {
      return { status: 'urgent', class: 'status-warning', text: 'Calibração Urgente' };
    } else if (diffDays <= 30) {
      return { status: 'warning', class: 'status-warning', text: 'Calibração Próxima' };
    } else {
      return { status: 'ok', class: 'status-success', text: 'Calibração em Dia' };
    }
  };

  if (isLoading) {
    return (
      <div className="page-container">
        <div className="page-header">
          <button className="btn btn-secondary" onClick={() => navigate('/equipamentos')}>
            <i className="fas fa-arrow-left"></i>
            Voltar
          </button>
          <h1>Carregando Equipamento...</h1>
        </div>
        <div style={{ textAlign: 'center', padding: '100px 20px' }}>
          <div className="loading-spinner-large"></div>
          <p>Carregando detalhes do equipamento...</p>
        </div>
      </div>
    );
  }

  if (error || !equipment) {
    return (
      <div className="page-container">
        <div className="page-header">
          <button className="btn btn-secondary" onClick={() => navigate('/equipamentos')}>
            <i className="fas fa-arrow-left"></i>
            Voltar
          </button>
          <h1>Erro ao Carregar</h1>
        </div>
        <div className="page-content">
          <div className="card">
            <div className="card-body" style={{ textAlign: 'center', padding: '50px 20px' }}>
              <i className="fas fa-exclamation-triangle" style={{ fontSize: '3rem', color: '#e74c3c', marginBottom: '20px' }}></i>
              <h3>{error || 'Equipamento não encontrado'}</h3>
              <p>O equipamento solicitado não pôde ser carregado.</p>
              <button className="btn btn-primary" onClick={() => navigate('/equipamentos')}>
                Voltar para Lista
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const calibrationStatus = getCalibrationStatus(equipment.proxima_calibracao);

  return (
    <ViewLayout
      title="Detalhes do Equipamento"
      subtitle={equipment.nome}
      entityId={equipment.id}
      isLoading={isLoading}
      editUrl={`/equipamentos/editar/${equipment.id}`}
      onBack={() => navigate('/equipamentos')}
    >
      {/* Navegação por Abas */}
      <div className="card" style={{ marginBottom: '20px' }}>
        <div className="card-header">
          <div className="tab-navigation">
            <button
              className={`tab-button ${activeTab === 'detalhes' ? 'active' : ''}`}
              onClick={() => setActiveTab('detalhes')}
            >
              <i className="fas fa-info-circle"></i>
              Detalhes do Equipamento
            </button>
            <button
              className={`tab-button ${activeTab === 'qrcode' ? 'active' : ''}`}
              onClick={() => setActiveTab('qrcode')}
            >
              <i className="fas fa-qrcode"></i>
              QR Code
            </button>
          </div>
        </div>
      </div>

      {/* Conteúdo da Aba Detalhes */}
      {activeTab === 'detalhes' && (
        <>
          {/* Informações Básicas */}
          <div style={{ marginBottom: '30px' }}>
            <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
              Informações Básicas
            </h3>

            <div className="info-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
              <div className="info-item">
                <label className="form-label">Nome</label>
                <span className="info-value">{equipment.nome}</span>
              </div>

              <div className="info-item">
                <label className="form-label">TAG</label>
                <span className="info-value">{equipment.tag || equipment.codigo || '-'}</span>
              </div>

              <div className="info-item">
                <label className="form-label">Tipo</label>
                <span className="info-value">{equipment.tipo}</span>
              </div>

              <div className="info-item">
                <label className="form-label">Localização</label>
                <span className="info-value">{equipment.localizacao}</span>
              </div>

              <div className="info-item">
                <label className="form-label">Status</label>
                <span className="info-value">
                  <span className={`status-badge ${getStatusClass(equipment.status)}`}>
                    {getStatusText(equipment.status)}
                  </span>
                </span>
              </div>

              <div className="info-item">
                <label className="form-label">Fabricante</label>
                <span className="info-value">{equipment.fabricante || '-'}</span>
              </div>

              <div className="info-item">
                <label className="form-label">Número de Série</label>
                <span className="info-value">{equipment.numero_serie || '-'}</span>
              </div>

              <div className="info-item">
                <label className="form-label">Capacidade</label>
                <span className="info-value">{equipment.capacidade || '-'}</span>
              </div>
            </div>
          </div>

          {/* Calibração */}
          {(equipment.ultima_calibracao || equipment.proxima_calibracao) && (
            <div style={{ marginBottom: '30px' }}>
              <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
                Calibração
              </h3>

              <div className="info-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
                <div className="info-item">
                  <label className="form-label">Última Calibração</label>
                  <span className="info-value">{formatDate(equipment.ultima_calibracao)}</span>
                </div>

                <div className="info-item">
                  <label className="form-label">Próxima Calibração</label>
                  <span className="info-value">{formatDate(equipment.proxima_calibracao)}</span>
                </div>

                {equipment.proxima_calibracao && (
                  <div className="info-item">
                    <label className="form-label">Status Calibração</label>
                    <span className="info-value">
                      <span className={`status-badge ${calibrationStatus.class}`}>
                        {calibrationStatus.text}
                      </span>
                    </span>
                  </div>
                )}

                {calibrationStatus.status === 'overdue' && (
                  <div className="info-item">
                    <label className="form-label">Alerta</label>
                    <span className="info-value" style={{ color: '#e74c3c' }}>
                      <i className="fas fa-exclamation-triangle" style={{ marginRight: '5px' }}></i>
                      Calibração atrasada - Equipamento não deve ser usado
                    </span>
                  </div>
                )}

                {calibrationStatus.status === 'urgent' && (
                  <div className="info-item">
                    <label className="form-label">Alerta</label>
                    <span className="info-value" style={{ color: '#f39c12' }}>
                      <i className="fas fa-clock" style={{ marginRight: '5px' }}></i>
                      Calibração urgente - Agendar imediatamente
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Observações */}
          {equipment.observacoes && (
            <div style={{ marginBottom: '30px' }}>
              <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
                Observações
              </h3>

              <div className="info-item">
                <label className="form-label">Observações</label>
                <p style={{ color: '#7f8c8d', lineHeight: '1.6', margin: 0, padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                  {equipment.observacoes}
                </p>
              </div>
            </div>
          )}

          {/* Informações de Auditoria */}
          <div style={{ marginBottom: '30px' }}>
            <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
              Informações de Auditoria
            </h3>

            <div className="info-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
              <div className="info-item">
                <label className="form-label">Criado em</label>
                <span className="info-value">{formatDateTime(equipment.criado_em)}</span>
              </div>

              <div className="info-item">
                <label className="form-label">Atualizado em</label>
                <span className="info-value">{formatDateTime(equipment.atualizado_em)}</span>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Conteúdo da Aba QR Code */}
      {activeTab === 'qrcode' && (
        <div className="card">
          <div className="card-body">
            <div style={{ marginBottom: '30px' }}>
              <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
                QR Code do Equipamento
              </h3>
              <p style={{ color: '#7f8c8d', marginBottom: '20px' }}>
                Use este QR Code para identificar o equipamento. Imprima e fixe no equipamento para facilitar a identificação durante os checklists.
              </p>

              {isLoadingQRCode ? (
                <div style={{ textAlign: 'center', padding: '50px' }}>
                  <i className="fas fa-spinner fa-spin" style={{ fontSize: '2rem', color: '#04C64F' }}></i>
                  <p style={{ marginTop: '15px', color: '#7f8c8d' }}>Gerando QR Code...</p>
                </div>
              ) : qrCodeData ? (
                <div ref={qrCodeRef} style={{ textAlign: 'center' }}>
                  <div style={{
                    display: 'inline-block',
                    border: '2px solid #ddd',
                    borderRadius: '10px',
                    padding: '30px',
                    backgroundColor: '#fff',
                    boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
                  }}>
                    <img
                      src={qrCodeData.qrcode}
                      alt={`QR Code do equipamento ${equipment?.nome}`}
                      style={{
                        width: '250px',
                        height: '250px',
                        display: 'block',
                        margin: '0 auto'
                      }}
                    />
                    <div style={{ marginTop: '20px' }}>
                      <p style={{
                        fontSize: '20px',
                        fontWeight: 'bold',
                        color: '#2c3e50',
                        marginBottom: '10px'
                      }}>
                        {equipment?.nome}
                      </p>
                      {(equipment?.tag || qrCodeData.equipamento.tag) && (
                        <p style={{
                          fontSize: '24px',
                          fontWeight: 'bold',
                          color: '#2c3e50',
                          backgroundColor: '#f8f9fa',
                          padding: '10px 20px',
                          borderRadius: '5px',
                          display: 'inline-block'
                        }}>
                          {equipment?.tag || qrCodeData.equipamento.tag}
                        </p>
                      )}
                      <p style={{ marginTop: '10px', color: '#7f8c8d' }}>
                        {equipment?.tipo}
                      </p>
                      {equipment?.localizacao && (
                        <p style={{ marginTop: '5px', color: '#7f8c8d' }}>
                          Local: {equipment.localizacao}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Botões de Ação */}
                  <div style={{ marginTop: '30px', display: 'flex', gap: '15px', justifyContent: 'center', flexWrap: 'wrap' }}>
                    <button
                      className="btn btn-primary"
                      onClick={handlePrintQRCode}
                      style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                    >
                      <i className="fas fa-print"></i>
                      Imprimir QR Code
                    </button>
                    <button
                      className="btn btn-secondary"
                      onClick={handleDownloadQRCode}
                      style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                    >
                      <i className="fas fa-download"></i>
                      Baixar Imagem
                    </button>
                    <button
                      className="btn btn-outline"
                      onClick={() => {
                        setQRCodeData(null);
                        loadQRCode();
                      }}
                      style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                    >
                      <i className="fas fa-sync-alt"></i>
                      Regenerar QR Code
                    </button>
                  </div>

                  {/* Informações adicionais */}
                  <div style={{
                    marginTop: '30px',
                    padding: '20px',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '8px',
                    textAlign: 'left'
                  }}>
                    <h4 style={{ color: '#2c3e50', marginBottom: '15px' }}>
                      <i className="fas fa-info-circle" style={{ marginRight: '8px', color: '#04C64F' }}></i>
                      Como usar o QR Code
                    </h4>
                    <ul style={{ color: '#7f8c8d', lineHeight: '1.8', paddingLeft: '20px' }}>
                      <li>Imprima o QR Code e fixe em local visível no equipamento</li>
                      <li>Durante o checklist, escaneie o QR Code para identificar automaticamente o equipamento</li>
                      <li>O QR Code contém informações de identificação únicas do equipamento</li>
                      <li>Recomendamos plastificar a impressão para maior durabilidade</li>
                    </ul>
                  </div>
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '50px' }}>
                  <i className="fas fa-exclamation-triangle" style={{ fontSize: '2rem', color: '#e74c3c' }}></i>
                  <p style={{ marginTop: '15px', color: '#7f8c8d' }}>Erro ao carregar QR Code</p>
                  <button
                    className="btn btn-primary"
                    onClick={loadQRCode}
                    style={{ marginTop: '15px' }}
                  >
                    Tentar novamente
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </ViewLayout>
  );
};

export default EquipmentView;
